/**
 * The watched-game BOOK lane.
 *
 * Measured on the fleet (2026-09-21): every (re)connect or filter change makes
 * an edge push a whole-label markets snapshot — NFL ≈ 9k rows, CFB ≈ 20k,
 * 2–3.6 MB — down the SAME socket as live order books, and the edge's
 * backpressure state is per socket. That backlog flips the consumer to level 1
 * and coalesces EVERY book on the socket to 250–1000 ms ("High backpressure —
 * book updates coalesced"); on a slow link it ends in a terminate/reconnect
 * loop. The books themselves were sub-second fresh at the edge the whole time.
 *
 * So while a trader is on a single-game page, that page's books ride their own
 * sockets: one per owning engine, `booksOnly` (never `markets`, never
 * `unabated`), direct, subscribed to exactly the page's tickers. A socket that
 * never asks for markets can never be handed a snapshot, so it never leaves
 * level 0 and its books stay per-delta.
 *
 * Ownership is NOT re-derived here: the market store routes each ticker to the
 * same engine the fleet would have used (`_routeOrderbookNeeds`) and hands the
 * result to this sink, keyed by URL — a game's markets can span several engines
 * (NFL / NFLTOTALS / NFLPROPS live on different edges). When ownership moves,
 * the store re-routes and the lane follows. The fleet keeps running for
 * everything else (markets rows, the board); it just no longer carries this
 * page's books.
 *
 * Escape hatch: `VITE_GAME_BOOK_LANE=off`, or per browser
 * `localStorage.g1xl_game_book_lane = 'off'` — books fall back to the fleet.
 */
import { HydraClient } from '@/clients/HydraClient';
import { feedGatewayEnabled } from '@/clients/feedFilterConfig';
import { useMarketStore } from '@/stores/marketStore';

/** A quiet pre-game book legitimately says nothing for a long time. */
const BOOK_LANE_STALL_MS = 120_000;
const SOURCE = 'book-lane';

/** An engine with no tickers this instant is usually about to get them back:
 *  sections unmount/remount and ownership is re-derived many times while a
 *  page settles. Closing on the first empty route tore down sockets that were
 *  still CONNECTING ("WebSocket is closed before the connection is
 *  established") and re-dialled them a frame later. Idle sockets linger. */
export const BOOK_LANE_IDLE_CLOSE_MS = 20_000;

const clients = new Map<string, HydraClient>();
const idleTimers = new Map<string, ReturnType<typeof setTimeout>>();
let refs = 0;

function cancelIdle(url: string): void {
  const timer = idleTimers.get(url);
  if (timer == null) return;
  clearTimeout(timer);
  idleTimers.delete(url);
}

export function gameBookLaneEnabled(): boolean {
  if (feedGatewayEnabled()) return false; // the gateway carries books on the tab's one socket
  try {
    const override = globalThis.localStorage?.getItem('g1xl_game_book_lane');
    if (override === 'off') return false;
    if (override === 'on') return true;
  } catch { /* storage unavailable — fall through to the env */ }
  return (import.meta.env?.VITE_GAME_BOOK_LANE ?? 'on') !== 'off';
}

function buildClient(url: string): HydraClient {
  const store = () => useMarketStore.getState();
  const client = new HydraClient(
    url,
    {
      onMarket: () => {},
      onConsensus: () => {},
      onStatus: () => {},
      onOrderbook: (ticker, book, age, observation) =>
        store().ingestOrderbook(ticker, book, SOURCE, age, observation),
    },
    { workerName: 'hydra-book-lane', booksOnly: true, tabStallThresholdMs: BOOK_LANE_STALL_MS },
  );
  client.start();
  return client;
}

function sink(byUrl: Map<string, string[]>): void {
  for (const [url, tickers] of byUrl) {
    let client = clients.get(url);
    if (!client) {
      client = buildClient(url);
      clients.set(url, client);
    }
    cancelIdle(url);
    client.setOrderbookNeeds(tickers);
  }
  // An engine that owns none of the page's books RIGHT NOW: unsubscribe at
  // once (no stale books), but keep the socket until it has stayed idle.
  for (const [url, client] of clients) {
    if (byUrl.has(url) || idleTimers.has(url)) continue;
    client.setOrderbookNeeds([]);
    idleTimers.set(url, setTimeout(() => {
      idleTimers.delete(url);
      clients.get(url)?.stop();
      clients.delete(url);
    }, BOOK_LANE_IDLE_CLOSE_MS));
  }
}

/** Hold the lane while a single-game page is mounted. Refcounted; returns the
 *  release. With the lane disabled this is a no-op and books ride the fleet. */
export function acquireGameBookLane(): () => void {
  if (!gameBookLaneEnabled()) return () => {};
  refs += 1;
  if (refs === 1) useMarketStore.getState().setBookLaneSink(sink);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    refs -= 1;
    if (refs > 0) return;
    // Hand the books back to the fleet FIRST, then close the lane sockets.
    useMarketStore.getState().setBookLaneSink(null);
    closeAll();
  };
}

/** Diagnostics: which engines the lane is connected to right now. */
export function gameBookLaneUrls(): string[] {
  return [...clients.keys()];
}

function closeAll(): void {
  for (const timer of idleTimers.values()) clearTimeout(timer);
  idleTimers.clear();
  for (const client of clients.values()) client.stop();
  clients.clear();
}

/** Tests only. */
export function resetGameBookLane(): void {
  closeAll();
  refs = 0;
}
